import { getSizedImageUrl } from '@shopify/theme-images'
import { productPrice, productPointsValue } from '../helpers/product'

import {
  defineStore
} from 'pinia'

export const useBundleStore = defineStore('BundleStore', {
  state: () => {
    return {
      bundle: {},
      bundleOptions: [],
      isBundle: false,
      slideoutOpen: false,
      activeCollectionIndex: 0,
      bundleQuantity: 1,
      isMetafieldsMode: false
    }
  },

  actions: {
    setupBundle () {

      this.bundle = {
        ...window.theme.bundle
      }

      this.bundle.selection = []
      if (window.theme?.bundle_options?.length) {
        this.isMetafieldsMode = true
        this.bundleOptions = [
          ...window.theme.bundle_options
        ]
      } else if (window.theme?.bundle?.collections?.length) {
        this.bundleOptions = [
          ...window.theme.bundle.collections
        ]
      } else {
        this.bundleOptions = []
        this.isMetafieldsMode = false
        this.isBundle = false
        return
      }

      this.isBundle = true

      this.bundleOptions.forEach((option, index) => {
        const availableProducts = option.products.filter(product => product.available && product.inventory_quantity > this.bundle.product.increment)
        const totalAvailable = availableProducts.reduce((total, product) => total + product.inventory_quantity, 0)
        let totalUsed = 0
        let initialProducts = []
        if (availableProducts.length && totalAvailable >= option.minimumQuantity) {
          while (initialProducts.length < option.minimumQuantity) {
            availableProducts.forEach(product => {
              product.used_quantity = product.used_quantity || 0 // track how many have been added
              if (initialProducts.length < option.minimumQuantity && product.inventory_quantity >= product.used_quantity) {
                initialProducts = [...initialProducts, ...Array.from({length: this.bundle.product.increment}, () => product)]
                product.used_quantity += this.bundle.product.increment
                totalUsed++
              }
            })
            if (totalUsed === totalAvailable) {
              break
            }
          }
        }
        this.bundleOptions[index].initialProducts = [...initialProducts]
        this.bundleOptions[index].selectedProducts = [...initialProducts]
      })
    },

    openSlideout (index) {
      this.activeCollectionIndex = index
      this.slideoutOpen = true
      document.body.classList.add('overflow-hidden')
      // Dispatch custom event when slideout opens
      document.dispatchEvent(new CustomEvent('hidePDPNavigationButtons', {
        detail: { source: 'bundle-customiser' }
      }))
    },
    closeSlideout (event = null) {
      if (!event || event.target.hasAttribute('data-close')) {
        this.slideoutOpen = false
        document.body.classList.remove('overflow-hidden')
        // Dispatch custom event when slideout closes
        document.dispatchEvent(new CustomEvent('showPDPNavigationButtons', {
          detail: { source: 'bundle-customiser' }
        }))
      }
    },

    changeProduct (productIndex) {
      const collection = this.isCollectionBundle ? this.bundleOptions[0] : this.bundleOptions[this.activeCollectionIndex]
      if (this.isCollectionBundle) {
        this.bundleOptions[0].selectedProducts[this.activeCollectionIndex] = collection.products[productIndex]
      } else {
        this.bundleOptions[this.activeCollectionIndex].selectedProducts = [collection.products[productIndex]]
      }
    },

    removeProductFromSelections (variantId) {
      // ensure array is sorted by variants
      this.bundleOptions[this.activeCollectionIndex].selectedProducts.sort((a,b) => {
        return a.variantId - b.variantId
      })
      const matchIndex = this.bundleOptions[this.activeCollectionIndex].selectedProducts.findIndex(product => (product.variantId ?? product.variants[0].id) === variantId)
      this.bundleOptions[this.activeCollectionIndex].selectedProducts.splice(matchIndex, this.bundle.product.increment)
    },

    addProductToSelections (productIndex) {
      const collection = this.bundleOptions[this.activeCollectionIndex]
      this.bundleOptions[this.activeCollectionIndex].selectedProducts = [...collection.selectedProducts, ...Array.from({ length: this.bundle.product.increment }, () => collection.products[productIndex])]
    },

    increaseBundleQuantity () {
      this.bundleQuantity += 1
    },
    decreaseBundleQuantity () {
      this.bundleQuantity -= 1
    },
  },

  getters: {
    bundlePrice () {
      let price = 0
      if (this.bundle?.product?.price && this.bundle.product.price !== 0) {
        return this.bundle.product.price
      }
      const bundleCollection = this.bundleOptions
      bundleCollection.forEach(collection => {
        collection.selectedProducts.forEach(product => {
          price += product.price
        })
        price -= (collection.discount * 100)
      })
      return price
    },

    isDuo () {
      return (this.bundleOptions.length === 1 && this.bundleOptions[0].minimumQuantity === 2 && this.bundleOptions[0].maximumQuantity === 2) ||
        (this.bundleOptions.length === 2 && this.bundleOptions[0].maximumQuantity === 1 && this.bundleOptions[0].productType === 'Tub' && this.bundleOptions[1].maximumQuantity === 1 && this.bundleOptions[1].productType === 'Tub')
    },

    isTrio () {
      return this.bundleOptions.length === 1  && this.bundleOptions[0].minimumQuantity == 3 && this.bundleOptions[0].maximumQuantity == 3
    },

    isCollectionBundle () {
      return this.bundleOptions.length === 1 && (this.bundleOptions[0].minimumQuantity === 2 || this.bundleOptions[0].minimumQuantity === 3)
    },

    sachetsCollection () {
      const collection = this.bundleOptions.filter(collection => {
        if (collection?.productType) {
          return collection?.productType === 'Sachet'
        }
        return collection.products.find(product => product.handle.includes('sachet')) !== undefined
      })
      return collection?.[0] ? collection[0] : null
    },

    otherCollections () {
      return this.bundleOptions.filter(collection => {
        if (collection?.productType) {
          return collection?.productType !== 'Sachet'
        }
        return !collection.products.find(product => product.handle.includes('sachet'))
      })
    },

    selectedProducts () {
      const selectedProducts = []
      const bundleCollection = this.bundleOptions
      bundleCollection.forEach(collection => selectedProducts.push([]))

      bundleCollection.forEach((collection, index) => {
        collection.selectedProducts.forEach(product => {
          const alreadyAddedProduct = selectedProducts[index].find(selectedProduct => selectedProduct.variantId === (product.variantId ?? product.variants[0].id))
          if (alreadyAddedProduct) {
            alreadyAddedProduct.quantity += 1
          } else {
            selectedProducts[index].push(
              {
                title: product.title,
                variantId: product.variantId ?? product.variants[0].id,
                quantity: 1,
                productType: collection.productType
              }
            )
          }
        })
      })
      return selectedProducts
    },

    enableAddToCart () {
      let enableAddToCart = true
      const bundleCollection = this.bundleOptions
      // @todo
      bundleCollection.forEach(collection => {
        if (collection.selectedProducts.length < collection.minimumQuantity) {
          enableAddToCart = false
          return
        }
      })
      return enableAddToCart
    },

    bundlePayload () {

      const bundleKey = () => {
        const str = this.selectedProducts.map(collection => {
          return collection.map(product => {
            return `${product.variantId}-${product.quantity}`
          }).join('-')
        }).join('-')

        let hash = 0
        for (let i = 0; i < str.length; i++) {
          hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0
        }
        return (hash >>> 0).toString(36)
      }

      const payload = {
        items: [
          {

            id: this.bundle.product.variantId,
            quantity: this.bundleQuantity,
            properties: this.isMetafieldsMode ? {
              _bundle_parent: true,
              _bundle_key: bundleKey(),
            } : {}
          }
        ]
      }

      /*
        Update bundle properties with each product
      */
      const bundleCollection = this.bundleOptions
      bundleCollection.forEach((collection, collectionIndex) => {
        collection.selectedProducts.forEach((product, i) => {
          /*
            If duo of two different type of tubs
            update index of second collection item
          */
          if (this.isDuo && this.bundleOptions.length === 2 && collectionIndex === 1) {
            i = 1
          }
          payload.items[0].properties['_' + `${collection.productType ?? collection.label} ${i + 1}`] = product.handle
          if (this.isMetafieldsMode) {
            if (!payload.items[0].properties['_bundle_options']?.length) {
              payload.items[0].properties['_bundle_options'] = []
            }
            payload.items[0].properties['_bundle_options'] = [...payload.items[0].properties['_bundle_options'], product.variantId ?? product.variants[0].id]
          }
        })
      })

      /*
        Add each product to payload
      */
      if (!this.isMetafieldsMode) {
        this.selectedProducts.forEach(collection => {
          collection.forEach(product => {
            payload.items.push({
              id: product.variantId,
              quantity: product.quantity * this.bundleQuantity,
              properties: {
                bundle: this.bundle.product.handle,
                _bundle_key: bundleKey()
              }
            })
          })
        })
      }

      return payload
    }
  }
})
